import React, { useCallback } from 'react'
import { ArrowCaret, ContentAttached } from '@fs/zion-icon'
import { ListItem, ResponsiveTableRow, Paragraph, Separator, usePageWidth, Button } from '@fs/zion-ui'
import { trackEvent } from '@fs/zion-analytics'
import { useAttachMatchTreeContext } from '@fs/zion-attach-match-tree-provider'
import { useQueryParams } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'
import {
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  getNestedSubelementsByType,
  getPersonName,
  getPersonRelationships,
  getPrimaryRelationshipText,
  getRelationshipToPrimaryPersonText,
  useSlsStateContext,
} from '@fs/zion-sls'
import useHoverHighlights from './useHoverHighlights'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2StateContext, useR2DispatchContext } from '../../../../providers/r2Provider/R2Context'
import FieldDisplay, { getFieldValues } from '../../../familyReview/components/eventReviewCard/FieldDisplay'

const RelationshipText = ({ person, images }) => {
  const [t] = useTranslation()
  const personRelationships = getPersonRelationships(person, images, false)
  return (
    <>
      {personRelationships.map((relationship, i) => {
        const otherPerson = getNestedOrFloatingElementById(images, relationship.otherPersonId)
        if (i < 4) {
          return (
            <Paragraph size="sm" key={relationship.id} dense>
              <strong>{relationship.label}: </strong>
              {getPersonName({ person: otherPerson })}
            </Paragraph>
          )
        }
        return null
      })}
      {personRelationships.length > 4 && (
        <Button linkName="See More" data-testid="see-more-button" color="gray" onClick={() => {}}>
          {t('family-review.field.display.toggleInfo.more', 'See More')}
        </Button>
      )}
    </>
  )
}

const EventText = ({ event }) => {
  if (!event || !event.subElements) return ''
  const dateFields = getFieldValues(event.subElements.filter((element) => element.elementType === 'DATE')).slice(0, 2)
  const placeFields = getFieldValues(event.subElements.filter((element) => element.elementType === 'PLACE')).slice(0, 2)
  return (
    <>
      {dateFields.map(({ displayLabel, value, id }) => (
        <FieldDisplay values={[{ displayLabel, value, id }]} key={id} />
      ))}
      {dateFields.length > 0 && <Separator size="xs" />}
      {placeFields.map(({ displayLabel, value, id }) => (
        <FieldDisplay values={[{ displayLabel, value, id }]} key={id} />
      ))}
    </>
  )
}

const NormalColumn = ({ person, type }) => {
  const subElements = getNestedSubelementsByType(person, type)
  const value = getMostRecentFieldValue(subElements.find((se) => se.fieldValues?.length > 0))
  return (
    (
      <Paragraph size="sm" dense>
        {value}
      </Paragraph>
    ) || ''
  )
}

const FarRightColumn = ({ primaryText }) => {
  return <ListItem primaryText={primaryText} endElement={<ListItem.Icon Icon={ArrowCaret} />} dense />
}

export default function PersonTableRowDisplay({
  person,
  record,
  i,
  primaryEvent,
  otherEventsFormatted,
  maxOtherEventColumns,
}) {
  const [t] = useTranslation()
  const pageWidth = usePageWidth()
  const sm = pageWidth({ default: true, md: false })
  const md = pageWidth({ default: true, lg: false })
  const lg = pageWidth({ default: true, xl: false })
  const xl = pageWidth({ default: true, xxl: false })
  const { setQuery } = useQueryParams()
  const r2State = useR2StateContext()
  const r2Dispatch = useR2DispatchContext()
  const zionSlsState = useSlsStateContext()
  const {
    highlightPersonNameHighlight,
    highlightRecordHighlight,
    resetPersonNameHighlights,
    resetRecordHighlights,
    getIsSelected,
  } = useHoverHighlights()
  const { currentImageArk } = zionSlsState
  const { openOverlay, topMostPanel } = useOverlayContext()
  const { getAttachmentDataByPersonId, getPossibleMatchDataByPersonId } = useAttachMatchTreeContext()
  const attachment = getAttachmentDataByPersonId(person?.id)
  const match = getPossibleMatchDataByPersonId(person?.id)
  const attachmentOrMatchId = attachment || match
  const allPersonsMentioned = getNestedSubelementsByType(record, 'PERSON')
  const recordPersons = record?.subElements.filter((se) => se.elementType === 'PERSON')
  const otherPersonsMentioned = allPersonsMentioned?.filter((p) => p?.partitionKey !== currentImageArk) || []
  const isLastRow = i === (recordPersons?.length || 0) - 1
  const hasOthersMentioned = otherPersonsMentioned?.length > 0
  const hideBottomDivider = !isLastRow || hasOthersMentioned
  const handleOnClick = () => {
    openOverlay('viewIndex')
    r2Dispatch({
      type: 'NAV_SELECT_TO_VIEW',
      value: { personId: r2State.highlightedPersonId, recordId: record?.id },
    }) // Need to use highlightedPersonId because we hovered over the person
    trackEvent({
      event_name: 'click_action',
      link_name: `Edit: Select Person`,
    })
    setQuery({ personArk: `/ark:/61903/${r2State.highlightedPersonId}` })
  }
  const getOtherEventColumns = useCallback(() => {
    if (!otherEventsFormatted?.length) return {}
    const columnValues = otherEventsFormatted
      ?.map((otherEvent, j) => {
        const personEvent = otherEvent.events.find((e) => e.superElements[0].id === person.id)
        if (j === otherEventsFormatted.length - 1) {
          return {
            [`column${j + 7}`]: <FarRightColumn primaryText={<EventText event={personEvent} />} />,
          }
        }
        return {
          [`column${j + 7}`]: <EventText event={personEvent} />,
        }
      })
      .slice(0, maxOtherEventColumns)
    return columnValues.reduce((acc, val) => ({ ...acc, ...val }), {})
  }, [otherEventsFormatted, person.id, maxOtherEventColumns])

  const getColumns = useCallback(() => {
    const raceOrColor = <NormalColumn person={person} type="RACE_OR_COLOR" />
    const sex = <NormalColumn person={person} type="SEX_CODE" />
    const age = <NormalColumn person={person} type="AGE" />
    const relationship = <RelationshipText person={person} images={zionSlsState.images} /> || ''
    if (sm) return { column2: raceOrColor, column3: <FarRightColumn primaryText={sex} /> }
    if (md) return { column2: raceOrColor, column3: sex, column4: <FarRightColumn primaryText={age} /> }
    if (lg)
      return {
        column2: raceOrColor,
        column3: sex,
        column4: age,
        column5: <FarRightColumn primaryText={relationship} />,
      }
    if (xl)
      return {
        column2: raceOrColor,
        column3: sex,
        column4: age,
        column5: relationship,
        column6: <FarRightColumn primaryText={<EventText event={primaryEvent} />} />,
      }
    return {
      column2: raceOrColor,
      column3: sex,
      column4: age,
      column5: relationship,
      column6: <EventText event={primaryEvent} />,
      ...getOtherEventColumns(),
    }
  }, [getOtherEventColumns, primaryEvent, person, zionSlsState.images, sm, md, lg, xl])
  const relationshipText = `${getRelationshipToPrimaryPersonText(person, zionSlsState.types, record, t)}`
  const primaryRelationshipText = getPrimaryRelationshipText(null, relationshipText)

  return (
    <ResponsiveTableRow
      // this is necessary to make each row highlight on hover
      to={() => {}}
      onClick={handleOnClick}
      onMouseEnter={() => {
        highlightPersonNameHighlight({ person })
        highlightRecordHighlight({ record })
      }}
      onMouseLeave={() => {
        if (!topMostPanel()) {
          resetPersonNameHighlights()
          resetRecordHighlights()
        }
      }}
      selected={getIsSelected({ record, person })}
      noBottomDivider={hideBottomDivider}
      name={person.id}
      data-testid="person-table-row"
      column1={
        <ListItem
          overline={primaryRelationshipText}
          primaryText={getPersonName({ person })}
          startElement={
            attachmentOrMatchId ? (
              <ListItem.Icon Icon={ContentAttached} data-testid={`source-linker-button-${attachmentOrMatchId}`} />
            ) : (
              <ListItem.Filler width={18} />
            )
          }
          emphasized={!!person?.primary}
          dense
        />
      }
      {...getColumns()}
    />
  )
}
